import {
  Button,
  ListGroup,
  Modal,
  Overlay,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addNewTarget, mergeTargetQuery, replaceTargetQuery } from 'src/actions';
import translations from 'src/lang';
import { StanleyQuerySuggestion } from 'src/modules/query-builder/components/stanley-query/api/types';
import { getTargets } from 'src/selectors/queryBuilder';
import { StanleyUseQueryModeSelector } from './StanleyUseQueryModeSelector';
import { USE_QUERY_MODES, UseQueryMode } from './StanleyUseQueryModeSelector';

const { STANLEY_QUERY: translation } = translations['en-US'];
const qbTranslation = translation.MODAL.USE_QUERY_MODAL;

interface StanleyUseQueryModalProps {
  isOpen: boolean;
  query: StanleyQuerySuggestion | null;
  onClose: () => void;
}

export const StanleyUseQueryModal: FC<StanleyUseQueryModalProps> = ({
  isOpen,
  query,
  onClose,
}) => {
  const dispatch = useDispatch();
  const targets = useSelector(getTargets);
  const targetSelected = useSelector(
    (state: { queryBuilder: { target_selected: string } }) =>
      state.queryBuilder.target_selected
  );
  const [selectedValue, setSelectedValue] = useState<UseQueryMode>(
    targets.length < 3
      ? USE_QUERY_MODES.CREATE_NEW_AUDIENCE
      : USE_QUERY_MODES.ADD_TO_AUDIENCE
  );

  const onChange = useCallback(setSelectedValue, [setSelectedValue]);
  const onUseQuery = useCallback(() => {
    if (!query) {
      return;
    }
    if (
      selectedValue === USE_QUERY_MODES.CREATE_NEW_AUDIENCE &&
      targets.length < 3
    ) {
      dispatch(
        addNewTarget({
          initialTargetQuery: query.query_json.target_query,
        })
      );
      onClose();
      return;
    }
    if (
      selectedValue === USE_QUERY_MODES.ADD_TO_AUDIENCE &&
      targetSelected
    ) {
      dispatch(
        mergeTargetQuery(targetSelected, query.query_json.target_query)
      );
      onClose();
      return;
    }
    if (
      selectedValue === USE_QUERY_MODES.OVERWRITE_EXISTING_AUDIENCE &&
      targetSelected
    ) {
      dispatch(
        replaceTargetQuery(targetSelected, query.query_json.target_query)
      );
      onClose();
    }
  }, [
    dispatch,
    onClose,
    query,
    selectedValue,
    targetSelected,
    targets.length,
  ]);

  return (
    <Overlay isOpen={isOpen}>
      <Modal
        data-test-id="stanley-use-query-modal"
        closable
        handleClose={onClose}
        title={qbTranslation.TITLE}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                key="use"
                size="small"
                variant="primary"
                onClick={onUseQuery}
              >
                {qbTranslation.BUTTONS.USE}
              </Button>,
              <Button
                key="close"
                size="small"
                variant="outlined"
                onClick={onClose}
              >
                {qbTranslation.BUTTONS.CLOSE}
              </Button>,
            ]}
          />
        }
      >
        {query && (
          <StanleyUseQueryModeSelector
            promptText={qbTranslation.MODE_PROMPT}
            createNewLabel={qbTranslation.CREATE_NEW_LABEL}
            name="use-query-as"
            selectedValue={selectedValue}
            onChange={onChange}
          />
        )}
      </Modal>
    </Overlay>
  );
};
